import React, {useEffect, useState} from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography, TextField, CircularProgress,
  Box
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiService from '@/service/api-service';
import {formatDate} from '@/utils/formatters';
import {useIsMobile} from "@/hooks/use-mobile.tsx";
import {licenseNumberPattern} from "@/utils/patterns.ts";

interface AssignmentData {
  id: string;
  uuid: string;
  carVin: string;
  deviceManufacturerNumber: string;
  installedAt: any;
  removedAt: any;
  customerNumber?: string;
  [key: string]: any;
}

interface AssignmentDetailDialogProps {
  assignment: AssignmentData | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateAssignmentDialog = ({ assignment, open, onClose, onSuccess }: AssignmentDetailDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseNumber, setLicenseNumber] = useState<string>('');

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      await apiService.post(`/customers/${assignment.customerNumber}/installations/${assignment.uuid}/finalize`, {
        licenseNumber,
      });

      onSuccess();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const enterLicenseNumber = (value: string) => {
    if (!licenseNumberPattern.test(value)) {
      setError(t('assistant.vehicleIdentificationStep.invalidLicenseNumber'))
    } else {
      setError(null);
    }

    setLicenseNumber(value);
  }

  if (!assignment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: 'background.paper' }}>
        {t('assignments.details')}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper', pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assistant.vehicleIdentificationStep.vin')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.carVin}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('devices.table.deviceSerial')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.deviceManufacturerNumber}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.installationDate')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(assignment.installedAt)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t('assignments.updateDialog.title')}
        </Typography>

        <TextField
          label={t('assignments.licenseNumber')}
          fullWidth
          value={licenseNumber}
          onChange={(e) => enterLicenseNumber(e.target.value)}
          error={!!error}
          helperText={error}
          variant="outlined"
          disabled={loading}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{
        bgcolor: 'background.paper',
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxSizing: 'border-box'
      }}>
          <Button onClick={onClose} color="secondary" disabled={loading}>
            {t('common.close')}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={loading || !licenseNumberPattern.test(licenseNumber)}
          >
            {loading ? <CircularProgress size={24} /> : t('common.save')}
          </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateAssignmentDialog;
